(() => {
  const SELECTOR = "[data-kenzu-header]";
  const controllers = new WeakMap();

  /**
   * Controlador visual del header Kenzu.
   *
   * Responsabilidades:
   * - Activar transparencia sobre el contenido.
   * - Detectar el estado de scroll.
   * - Aplicar las clases visuales correspondientes.
   * - Mantener actualizada la altura CSS del header.
   *
   * El comportamiento sticky nativo continúa gestionado
   * por el custom element <sticky-header> de Shopify.
   */
  class KenzuHeaderController {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.section = wrapper.closest(".section-header");

      if (!this.section) return;

      this.transparentMode =
        wrapper.dataset.transparentMode || "never";

      this.isHome =
        wrapper.dataset.isHome === "true";

      this.stickyType =
        wrapper.dataset.stickyType || "none";

      this.stickyEnabled =
        this.stickyType !== "none";

      this.overlayEnabled =
        this.transparentMode === "all" ||
        (
          this.transparentMode === "home" &&
          this.isHome
        );

      this.scrollThreshold =
        this.parseScrollThreshold(
          wrapper.dataset.scrollThreshold
        );

      this.ticking = false;
      this.destroyed = false;
      this.resizeObserver = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      this.initialize();
    }

    /**
     * Convierte el umbral configurado en un número válido.
     * Respeta correctamente el valor 0.
     */
    parseScrollThreshold(value) {
      const parsedValue = Number(value);

      if (!Number.isFinite(parsedValue)) {
        return 10;
      }

      return Math.max(0, parsedValue);
    }

    /**
     * Inicializa clases, eventos y observadores.
     */
    initialize() {
      this.section.classList.add(
        "kenzu-header-section"
      );

      this.section.classList.toggle(
        "kenzu-header-overlay-enabled",
        this.overlayEnabled
      );

      this.section.classList.toggle(
        "kenzu-header-sticky-enabled",
        this.stickyEnabled
      );

      this.updateState();

      window.addEventListener(
        "scroll",
        this.onScroll,
        { passive: true }
      );

      window.addEventListener(
        "resize",
        this.onResize,
        { passive: true }
      );

      this.initializeResizeObserver();
    }

    /**
     * Observa cambios reales de dimensiones.
     *
     * Esto cubre:
     * - carga del logo;
     * - carga de fuentes;
     * - cambios del personalizador;
     * - cambios de altura del header.
     */
    initializeResizeObserver() {
      if (!("ResizeObserver" in window)) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeaderHeight();
      });

      this.resizeObserver.observe(this.wrapper);
    }

    /**
     * Agrupa las actualizaciones de scroll en un único
     * requestAnimationFrame para evitar cálculos excesivos.
     */
    onScroll() {
      if (this.ticking || this.destroyed) return;

      this.ticking = true;

      window.requestAnimationFrame(() => {
        if (!this.destroyed) {
          this.updateState();
        }

        this.ticking = false;
      });
    }

    /**
     * Actualiza dimensiones al cambiar el viewport.
     */
    onResize() {
      if (this.destroyed) return;

      this.updateHeaderHeight();
    }

    /**
     * Calcula y aplica el estado visual actual.
     */
    updateState() {
      if (this.destroyed) return;

      const scrollTop = this.getScrollTop();

      const passedThreshold =
        scrollTop > this.scrollThreshold;

      /*
       * Solo aplicamos el estado sticky visual cuando:
       * - el sticky está activado; o
       * - el header transparente necesita recuperar fondo.
       *
       * Esto evita convertir accidentalmente un header con
       * sticky desactivado en un elemento sticky.
       */
      const visualScrolled =
        passedThreshold &&
        (
          this.stickyEnabled ||
          this.overlayEnabled
        );

      const overlayActive =
        this.overlayEnabled &&
        !passedThreshold;

      this.section.classList.toggle(
        "kenzu-header-is-scrolled",
        visualScrolled
      );

      this.section.classList.toggle(
        "kenzu-header-overlay-active",
        overlayActive
      );

      this.wrapper.classList.toggle(
        "is-scrolled",
        visualScrolled
      );

      this.wrapper.classList.toggle(
        "is-overlay-active",
        overlayActive
      );

      this.updateHeaderHeight();
    }

    /**
     * Devuelve una posición de scroll segura.
     */
    getScrollTop() {
      return (
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0
      );
    }

    /**
     * Publica la altura del encabezado como variable CSS.
     */
    updateHeaderHeight() {
      if (this.destroyed) return;

      const height =
        Math.round(
          this.wrapper.getBoundingClientRect().height
        ) || 0;

      document.documentElement.style.setProperty(
        "--kenzu-header-height",
        `${height}px`
      );
    }

    /**
     * Elimina listeners y observadores.
     *
     * Es imprescindible en el editor de Shopify porque las
     * secciones pueden descargarse y volver a montarse.
     */
    destroy() {
      if (this.destroyed) return;

      this.destroyed = true;

      window.removeEventListener(
        "scroll",
        this.onScroll
      );

      window.removeEventListener(
        "resize",
        this.onResize
      );

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      this.wrapper.removeAttribute(
        "data-kenzu-initialized"
      );

      controllers.delete(this.wrapper);
    }
  }

  /**
   * Inicializa todos los headers que todavía no tengan
   * controlador.
   */
  const initializeHeaders = (root = document) => {
    const wrappers = [];

    if (
      root instanceof Element &&
      root.matches(SELECTOR)
    ) {
      wrappers.push(root);
    }

    if (
      root instanceof Document ||
      root instanceof DocumentFragment ||
      root instanceof Element
    ) {
      wrappers.push(
        ...root.querySelectorAll(SELECTOR)
      );
    }

    wrappers.forEach((wrapper) => {
      if (controllers.has(wrapper)) return;

      wrapper.dataset.kenzuInitialized = "true";

      const controller =
        new KenzuHeaderController(wrapper);

      if (!controller.section) {
        wrapper.removeAttribute(
          "data-kenzu-initialized"
        );

        return;
      }

      controllers.set(wrapper, controller);
    });
  };

  /**
   * Destruye los controladores contenidos dentro de una
   * sección eliminada por el editor.
   */
  const destroyHeaders = (root) => {
    if (!root) return;

    const wrappers = [];

    if (
      root instanceof Element &&
      root.matches(SELECTOR)
    ) {
      wrappers.push(root);
    }

    if (root instanceof Element) {
      wrappers.push(
        ...root.querySelectorAll(SELECTOR)
      );
    }

    wrappers.forEach((wrapper) => {
      const controller = controllers.get(wrapper);

      if (controller) {
        controller.destroy();
      }
    });
  };

  const boot = () => {
    initializeHeaders(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  /**
   * Eventos del editor visual de Shopify.
   */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeHeaders(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroyHeaders(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:reorder",
    () => {
      initializeHeaders(document);
    }
  );
})();